/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Slash Command Popup Menu Component (Task 10.2.1 & 10.7.2)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import type { SlashCommandItem } from './slash-commands';
import { useI18n } from '../../../stores/useI18nStore';

export interface SlashCommandMenuProps {
  items: SlashCommandItem[];
  command: (item: SlashCommandItem) => void;
}

export interface SlashCommandMenuRef {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean;
}

const COMMAND_KEY_MAP: Record<string, string> = {
  'ai-write-prompt': 'aiPrompt',
  'ai-summary': 'aiSummary',
  'heading-1': 'heading1',
  'heading-2': 'heading2',
  'heading-3': 'heading3',
  'bullet-list': 'bulletList',
  'numbered-list': 'numberedList',
  'task-list': 'taskList',
  'blockquote': 'quote',
  'code-block': 'codeBlock',
  'table': 'table',
  'horizontal-rule': 'divider',
  'image': 'image',
};

export const SlashCommandMenu = forwardRef<SlashCommandMenuRef, SlashCommandMenuProps>(
  ({ items, command }, ref) => {
    const [selectedIndex, setSelectedIndex] = useState(0);
    const { t } = useI18n();

    useEffect(() => {
      setSelectedIndex(0);
    }, [items]);

    const selectItem = (index: number) => {
      const item = items[index];
      if (item) {
        command(item);
      }
    };

    useImperativeHandle(ref, () => ({
      onKeyDown: ({ event }) => {
        if (event.key === 'ArrowUp') {
          setSelectedIndex((prev) => (prev + items.length - 1) % items.length);
          return true;
        }

        if (event.key === 'ArrowDown') {
          setSelectedIndex((prev) => (prev + 1) % items.length);
          return true;
        }

        if (event.key === 'Enter') {
          selectItem(selectedIndex);
          return true;
        }

        return false;
      },
    }));

    if (items.length === 0) {
      return (
        <div className="z-50 min-w-[280px] overflow-hidden rounded-xl border border-surface-border bg-surface p-3 shadow-glass text-xs text-foreground-muted">
          {t('editor.slash.noMatch')}
        </div>
      );
    }

    return (
      <div className="z-50 min-w-[300px] max-w-[340px] max-h-[320px] overflow-y-auto rounded-xl border border-surface-border bg-surface p-1.5 shadow-glass backdrop-blur-xl animate-scale-in">
        <div className="px-2 py-1.5 text-[10px] font-bold uppercase tracking-wider text-foreground-muted">
          {t('editor.slash.menuHeading')}
        </div>
        <div className="space-y-0.5">
          {items.map((item, index) => {
            const isSelected = index === selectedIndex;
            const Icon = item.icon;
            const isAi = item.category === 'ai';

            const key = COMMAND_KEY_MAP[item.id];
            const title = key ? t(`editor.slash.${key}` as any, undefined, item.title) : item.title;
            const description = key ? t(`editor.slash.${key}Desc` as any, undefined, item.description) : item.description;

            return (
              <button
                key={item.id}
                type="button"
                onClick={() => selectItem(index)}
                className={`w-full flex items-start gap-2.5 px-2.5 py-2 rounded-lg text-left transition-colors duration-150 ${
                  isSelected
                    ? isAi
                      ? 'bg-primary/15 text-primary'
                      : 'bg-muted text-foreground'
                    : 'text-foreground-muted hover:bg-muted/60 hover:text-foreground'
                }`}
              >
                <div
                  className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${
                    isAi
                      ? 'bg-primary/20 text-primary'
                      : isSelected
                      ? 'bg-surface-elevated text-foreground'
                      : 'bg-muted text-foreground-muted'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                    <span>{title}</span>
                    {isAi && (
                      <span className="text-[9px] px-1.5 py-0.2 rounded-full font-bold bg-primary text-primary-fg uppercase tracking-wide">
                        AI
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-foreground-muted line-clamp-1 leading-snug">
                    {description}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  },
);

SlashCommandMenu.displayName = 'SlashCommandMenu';
